import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UploadSimple, Sparkle, PawPrint, ArrowRight, ArrowLeft, Lightning, MagicWand, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { fbTrack } from "@/lib/fbpixel";
import { useAuth } from "@/context/AuthContext";

const GARMENTS = [
  { key: "hoodie", label: "Oversized Hoodie", price: 149, blurb: "Heavyweight camo-lined hood" },
  { key: "tee", label: "Premium Tee", price: 99, blurb: "Extended-back, boxy fit" },
  { key: "bandana", label: "Reflective Bandana", price: 59, blurb: "Night-glow dog gear" },
];

const HOW = [
  { n: "01", t: "UPLOAD YOUR DOG", d: "One clear photo. That's the whole ask.", icon: UploadSimple },
  { n: "02", t: "AI BUILDS THE COLLAB", d: "Neon graffiti, hypercolor drip, Wigglez signature — in seconds.", icon: MagicWand },
  { n: "03", t: "WEAR IT · FUND THE PACK", d: "Order your 1-of-1. Every drop runs for a reason.", icon: Lightning },
];

export default function Collab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState("");
  const [uploadPath, setUploadPath] = useState("");
  const [petName, setPetName] = useState("");
  const [garment, setGarment] = useState("hoodie");
  const [busy, setBusy] = useState(false);
  const [collab, setCollab] = useState(null);
  const [aiBlocked, setAiBlocked] = useState(false);

  const requireAuth = () => {
    if (!user) { toast.error("Sign in to create your 1-of-1"); navigate("/auth"); return false; }
    return true;
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!requireAuth()) return;
    setPreview(URL.createObjectURL(f));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadPath(data.path);
      toast.success("Photo locked in 🐾");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setPreview("");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!requireAuth()) return;
    if (!uploadPath) return toast.error("Upload a pet photo first");
    if (!petName.trim()) return toast.error("Give your pet a name");
    setBusy(true);
    setAiBlocked(false);
    setStep(3);
    try {
      const { data } = await api.post("/collab/generate", { image_path: uploadPath, pet_name: petName, garment });
      setCollab(data);
      toast.success("Your 1-of-1 is ready 🔥");
    } catch (err) {
      setAiBlocked(true);
    } finally { setBusy(false); }
  };

  const order = async () => {
    if (!collab) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/collab-checkout", { collab_id: collab.collab_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setBusy(false);
    }
  };

  const notifyMe = async () => {
    try {
      await api.post("/subscribe", { email: user?.email, source: "collab-waitlist" });
      fbTrack("Lead", { content_name: "collab-waitlist" });
      toast.success("You're on the list — we'll ping you the second the studio reopens.");
    } catch { toast.success("You're on the list."); }
  };

  const artUrl = collab ? `${API}/collab-image/${collab.art_path}` : "";
  const gPrice = GARMENTS.find((g) => g.key === garment)?.price || 149;

  return (
    <div data-testid="collab-page">
      {/* GAME HERO */}
      <section className="relative overflow-hidden border-b border-white/10 bg-[#050505]">
        <div className="absolute inset-0 pointer-events-none opacity-50"
          style={{ background: "radial-gradient(70% 130% at 50% -10%, rgba(255,20,147,0.20), transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-10 text-center">
          <p className="overline text-primary mb-3 inline-flex items-center gap-2"><Sparkle weight="fill" size={14} /> 1-of-1 Collab Creator · The Game</p>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-6xl lg:text-7xl">
            Upload your dog. <span className="neon-text">AI builds the collab.</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mt-5">
            Drop one photo of your pit. Our AI reworks it into a neon Mr. Wigglez graffiti masterpiece and stamps it on your gear — auto-signed <span className="text-secondary">Mr. Wigglez x [your pet]</span>. Wear the art that funds the ranch.
          </p>

          {/* 3-step game board */}
          <div className="grid sm:grid-cols-3 gap-4 mt-10 text-left">
            {HOW.map((h, i) => {
              const Icon = h.icon;
              const done = step > i + 1 || (step === 3 && collab);
              const active = step === i + 1;
              return (
                <motion.div key={h.n} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className={`relative rounded-2xl border p-5 transition-colors ${active ? "border-primary bg-primary/10 neon-glow" : done ? "border-secondary/40 bg-secondary/5" : "border-white/10 bg-card"}`}
                  data-testid={`collab-how-${i + 1}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-display font-black text-3xl text-white/15">{h.n}</span>
                    {done ? <CheckCircle weight="fill" size={22} className="text-secondary" /> : <Icon weight="fill" size={22} className={active ? "text-primary" : "text-muted-foreground"} />}
                  </div>
                  <p className="font-display font-black uppercase tracking-tight text-lg leading-tight">{h.t}</p>
                  <p className="text-sm text-muted-foreground mt-1.5">{h.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* LEFT — controls */}
          <div>
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="collab-step-1">
                <p className="overline text-primary mb-3 inline-flex items-center gap-2"><UploadSimple size={15} /> Step 1 · Drop the photo</p>
                <button onClick={() => fileRef.current?.click()} disabled={busy}
                  className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-primary/40 grid place-items-center gap-3 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-60"
                  data-testid="collab-upload-btn">
                  <UploadSimple size={44} className="text-primary tail-wiggle" />
                  <span className="font-display font-black uppercase tracking-tight text-xl">{busy ? "Uploading…" : "Tap to upload your dog"}</span>
                  <span className="text-xs text-muted-foreground">JPG or PNG · a clear, front-on shot works best</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="collab-file-input" />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="collab-step-2">
                <p className="overline text-primary mb-3 inline-flex items-center gap-2"><MagicWand size={15} /> Step 2 · Name & gear</p>
                <label className="overline text-muted-foreground">Pet name</label>
                <input value={petName} onChange={(e) => setPetName(e.target.value)} placeholder="e.g. King"
                  className="w-full mt-1.5 mb-6 bg-transparent border border-white/15 rounded-xl px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="collab-petname" />
                <div className="grid gap-3 mb-6">
                  {GARMENTS.map((g) => (
                    <button key={g.key} onClick={() => setGarment(g.key)} data-testid={`collab-garment-${g.key}`}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-colors ${garment === g.key ? "border-primary bg-primary/10" : "border-white/15 hover:border-white/30"}`}>
                      <span>
                        <span className="block font-modern">{g.label}</span>
                        <span className="block text-xs text-muted-foreground">{g.blurb}</span>
                      </span>
                      <span className="text-lg font-modern">${g.price}</span>
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-5 py-3 rounded-[10px] border border-white/15 text-muted-foreground hover:text-white" data-testid="collab-back-2">
                    <ArrowLeft weight="bold" size={16} /> Back
                  </button>
                  <button onClick={generate} disabled={busy} className="metal-cta inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-display font-black uppercase tracking-tight neon-glow disabled:opacity-50" data-testid="collab-generate">
                    <MagicWand weight="fill" size={18} /> Build my collab
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="collab-step-3">
                <p className="overline text-primary mb-3 inline-flex items-center gap-2"><Lightning size={15} /> Step 3 · Your drop</p>
                {busy ? (
                  <div className="rounded-2xl border border-primary/30 p-8 text-center neon-glow" data-testid="collab-generating">
                    <div className="mx-auto h-14 w-14 rounded-full border-2 border-primary border-t-transparent animate-spin mb-4" />
                    <p className="font-display font-black uppercase tracking-tight text-xl">The AI is painting {petName || "your pit"}…</p>
                    <p className="text-sm text-muted-foreground mt-1">Neon graffiti · hypercolor drip · ~15–30s</p>
                  </div>
                ) : aiBlocked ? (
                  <div className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-8" data-testid="collab-ai-blocked">
                    <WarningCircle weight="fill" size={30} className="text-amber-400 mb-3" />
                    <p className="font-display font-black uppercase tracking-tight text-2xl mb-2">The AI studio is warming up.</p>
                    <p className="text-sm text-muted-foreground mb-6">
                      Our 1-of-1 art engine is at capacity right now. Your photo of <span className="text-white">{petName || "your pit"}</span> is saved — get on the list and we'll build your <span className="text-secondary">${gPrice} {garment}</span> collab the second it reopens.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button onClick={notifyMe} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="collab-notify">
                        <Sparkle weight="fill" size={16} /> Notify me when it's live
                      </button>
                      <button onClick={() => { setStep(2); setAiBlocked(false); }} className="inline-flex items-center gap-2 px-5 py-3 rounded-[10px] border border-white/15 text-muted-foreground hover:text-white" data-testid="collab-back-blocked">
                        <ArrowLeft weight="bold" size={16} /> Change gear
                      </button>
                    </div>
                  </div>
                ) : collab ? (
                  <div data-testid="collab-result">
                    <p className="font-display font-black uppercase tracking-tight text-3xl mb-1">{collab.signature}</p>
                    <p className="text-muted-foreground text-sm mb-6 capitalize">1-of-1 {collab.garment} · ${collab.price}</p>
                    <div className="flex gap-3">
                      <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-5 py-3 rounded-[10px] border border-white/15 text-muted-foreground hover:text-white" data-testid="collab-redo">
                        <ArrowLeft weight="bold" size={16} /> Tweak
                      </button>
                      <button onClick={order} disabled={busy} className="metal-cta inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-display font-black uppercase tracking-tight neon-glow disabled:opacity-50" data-testid="collab-order">
                        <Lightning weight="fill" size={18} /> Order this collab · ${collab.price}
                      </button>
                    </div>
                  </div>
                ) : null}
              </motion.div>
            )}
          </div>

          {/* RIGHT — live preview */}
          <div className="relative">
            <div className="sticky top-24">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-card border border-white/10 neon-glow grid place-items-center" data-testid="collab-preview">
                <AnimatePresence mode="wait">
                  {artUrl ? (
                    <motion.img key="art" src={artUrl} alt={collab?.signature} initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }}
                      className="h-full w-full object-cover" data-testid="collab-art" />
                  ) : preview ? (
                    <motion.img key="prev" src={preview} alt="your pet" initial={{ opacity: 0 }} animate={{ opacity: 0.85 }}
                      className="h-full w-full object-cover" />
                  ) : (
                    <motion.div key="empty" className="text-center text-muted-foreground px-6">
                      <PawPrint size={50} className="mx-auto mb-3 text-primary/60 tail-wiggle" />
                      <p className="font-display font-black uppercase tracking-tight text-lg">Your art appears here</p>
                      <p className="text-xs mt-1">Upload a photo to watch the collab come alive.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                {collab && (
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 overline px-3 py-1.5 rounded-full bg-black/70 backdrop-blur text-secondary border border-secondary/30" data-testid="collab-signature">
                    {collab.signature}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
